import React, { useEffect, useMemo } from 'react';
import Filters from '../../../components/Filter';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { translate } from '../../../services/translate';
import { NavigationProp, RouteProp, useNavigation } from '@react-navigation/native';
import { ThirdPartyApprovedCompaniesParamList } from '../../../typings/Params';
import moment from 'moment';
import MyDateRangePicker from '../../../components/MyDateRangePicker';
import { useForm } from 'react-hook-form';
import { companyType, CompanyType, inscriptionStatus, InscriptionStatus } from '../../../services/central-api/third-party-approved-companies';
import FormDropDownPicker from '../../../components/FormDropDownPicker';
import { getInscriptionStatusBgColor, getInscriptionStatusLabel, getInscriptionStatusTextColor } from '../InscriptionsCommons';
import { useStyles } from 'react-native-unistyles';

interface Props {
    filters: ThirdPartyApprovedCompaniesParamList['InscriptionsManagement'];
    setFilters: React.Dispatch<React.SetStateAction<ThirdPartyApprovedCompaniesParamList['InscriptionsManagement']>>;
    authenticatedUser?: AuthenticatedUser;
    textFilter: string;
    setTextFilter: (value: string) => void;
}

type ValuePiece = Date | null;

interface FiltersForm {
    period?: { from: ValuePiece; to: ValuePiece; };
    status?: InscriptionStatus[];
    companyType?: Exclude<CompanyType, 'both'>[];
}

function Filter({ filters, setFilters, textFilter, setTextFilter }: Props) {
    const { theme } = useStyles();
    const navigation = useNavigation<NavigationProp<RouteProp<ThirdPartyApprovedCompaniesParamList, 'InscriptionsManagement'>>>();

    const defaultEmptyValues: FiltersForm = {
        period: {
            from: null,
            to: null,
        },
        companyType: [],
        status: []
    };

    const form = useForm<FiltersForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    useEffect(() => {

        form.reset({
            period: {
                from: filters.beginIn ? new Date(filters.beginIn) : null,
                to: filters.endIn ? new Date(filters.endIn) : null,
            },
            companyType: filters.companyType || [],
            status: filters.status || []
        });

    }, []);

    const handleCleanFilter = () => {
        setTextFilter('');
        form.reset(defaultEmptyValues);
    };

    const handleFilter = () => {
        const values = form.getValues();

        navigation.setParams({
            ...filters,
            page: 1,
            textFilter: textFilter !== '' ? textFilter : undefined,
            beginIn: values.period?.from ? values.period?.from.getTime() : undefined,
            endIn: values.period?.to ? values.period?.to.getTime() : undefined,
            companyType: values.companyType?.length ? values.companyType : undefined,
            status: values.status?.length ? values.status : undefined,
        });

        setFilters({
            ...filters,
            page: 0,
            textFilter: textFilter !== '' ? textFilter : undefined,
            beginIn: values.period?.from ? values.period?.from.getTime() : undefined,
            endIn: values.period?.to ? values.period?.to.getTime() : undefined,
            companyType: values.companyType?.length ? values.companyType : undefined,
            status: values.status?.length ? values.status : undefined,
        });
    };

    const companyTypeItems = useMemo(() => {
        return Object.values(companyType)
            .filter(v => v != 'both')
            .map((val) => ({
                label: translate(`${val}InscriptionType`),
                value: val,
                color: theme.colors.primary['500'],
                textColor: theme.colors.white['500']
            }));
    }, []);

    const inscriptionsStatusItems = useMemo(() => {
        return Object.values(inscriptionStatus)
            .map((val) => ({
                label: translate(getInscriptionStatusLabel(val)),
                value: val,
                color: getInscriptionStatusBgColor(val),
                textColor: getInscriptionStatusTextColor(val)
            }));
    }, []);

    return (
        <Filters
            onCleanFilters={handleCleanFilter}
            onFilters={handleFilter}
        >
            <FilterInput
                label={translate('typeToSearch')}
                value={textFilter}
                placeholder={translate('typeToSearch')}
                onChange={setTextFilter}
            />

            <FormDropDownPicker
                label={translate('status')}
                items={inscriptionsStatusItems}
                zIndex={3}
                multiple
                externalSelect
                fieldName={'status'}
                form={form}
            />

            <FormDropDownPicker
                label={translate('companyType')}
                items={companyTypeItems}
                multiple
                externalSelect
                zIndex={2}
                fieldName={'companyType'}
                form={form}
            />

            <MyDateRangePicker
                label={translate('period')}
                maxDate={moment().toDate()}
                form={form}
                fieldName='period'
                zIndex={1}
            />

        </Filters>
    );
}

export default Filter;
